import cx from "classnames"
import PropTypes from "prop-types"
import React, { useEffect, useMemo } from "react"
import { useBanners } from "effects/useSiteContent"
import { trackHubspotPageLoad } from "lib/hubspot"
import Footer from "components/ui/Footer"
import Header from "components/ui/Header"

function Layout({
  children,
  headerDark,
  headerFloating,
  hideHeaderAndFooter,
  contentSpaced,
  trackingName,
  pageName,
  pagePath,
  pageContentType = "site-page",
}) {
  /** Page banners from local YAML — resolved synchronously so the
   *  banner space is reserved on the very first render (SSR + first
   *  client paint), instead of appearing after a useEffect tick and
   *  pushing the rest of the page down. */
  const banners = useBanners()
  const pageBanner = useMemo(
    () => banners?.find((b) => b.path === pagePath) || null,
    [banners, pagePath]
  )

  const trackingSuffix = trackingName ? `_${trackingName}` : ""
  const trackSignupButtonClick = `Try for Free (header)${trackingSuffix}`

  useEffect(() => {
    trackHubspotPageLoad(["setContentType", pageContentType])
  }, [pageContentType])

  return (
    <>
      {!hideHeaderAndFooter && (
        <Header
          dark={headerDark}
          floating={headerFloating}
          trackSignupButtonClick={trackSignupButtonClick}
          pageName={pageName}
          pageBanner={pageBanner}
        />
      )}
      <main
        className={cx(
          { "floating-header": headerFloating },
          "w-screen overflow-hidden"
        )}
      >
        {contentSpaced ? (
          <div className="space-y-20">{children}</div>
        ) : (
          children
        )}
      </main>
      {!hideHeaderAndFooter && <Footer />}
    </>
  )
}

Layout.propTypes = {
  /** If true, header is going to use light colos */
  headerDark: PropTypes.bool,
  /** If true, the header will appear to be floating on top of the content area */
  headerFloating: PropTypes.bool,
  /** If true, hides header and footer */
  hideHeaderAndFooter: PropTypes.bool,
  /** Tracking namespace - if set, the signup click / submit events will be scoped to this name */
  trackingName: PropTypes.string,
  /** If true, adds space among sections */
  contentSpaced: PropTypes.bool,
  /** Page name */
  pageName: PropTypes.string,
  /** Page path */
  pagePath: PropTypes.string,
  children: PropTypes.node.isRequired,
}

export default Layout
